// Deterministic layout for the Infra map: inventory resources → React Flow
// nodes + edges, read left → right the way a request travels:
//
//   Client → Edge (hosting, CDN, auth) → VPC [ ALB · subnets (NAT inside) ·
//   services · endpoints / security groups / add-on stacks ] → Data &
//   storage (Firestore, S3, ECR, logs, secrets, build, mail) → External
//   (control plane, model providers, coding agents)
//
// A self-host stamp lays out as Client → Host [ compose services ] →
// External. Positions are computed, never stored — no layout library (the
// ibkr_api canvas this is modelled on dropped ELK for a grid too).
// Containment uses React Flow parentId + extent:'parent' so a VPC or subnet
// is a real group you can read as a boundary.

export const NODE_W = 200;
export const NODE_H = 64;
const GAP = 14;
const PAD = 16;
const HEAD = 30;

// Resources that live in the inspector, not on the canvas (route tables are
// listed on their subnet; target groups ride on the ALB card).
const HIDDEN_PREFIXES = ['rtb:', 'tg:'];

export function levelOf(status) {
  const s = String(status || '').toLowerCase();
  if (['running', 'ok', 'healthy', 'active', 'available'].includes(s)) return 'ok';
  if (['degraded', 'pending', 'starting', 'warn'].includes(s)) return 'warn';
  if (['stopped', 'failed', 'down', 'missing', 'error'].includes(s)) return 'down';
  return 'off';
}

function laneOf(r) {
  if (r.id === 'client') return 'client';
  if (r.provider === 'external') return 'external';
  if (r.provider === 'docker') return 'vpc';
  if (r.provider === 'firebase') return r.id === 'firestore' ? 'data' : 'edge';
  if (r.id === 'cdn' || r.category === 'edge') return 'edge';
  if (r.parent === 'vpc' || r.id === 'vpc' || r.id === 'alb' || r.id.startsWith('subnet:') || r.id.startsWith('nat:') || r.id.startsWith('cfn:') || (r.addon && r.category === 'network')) return 'vpc';
  if (['compute'].includes(r.category) && !r.addon) return 'vpc';
  return 'data';
}

const costFor = (costs, id) => (costs?.estimate?.byResource || []).find((x) => x.id === id) || null;

function card(r, costs, x, y, parentId) {
  const c = costFor(costs, r.id);
  const level = r.liveStatus ? levelOf(r.liveStatus.status) : r.drift === 'missing' ? 'down' : 'off';
  return {
    id: r.id,
    type: 'infraCard',
    position: { x, y },
    ...(parentId ? { parentId, extent: 'parent' } : {}),
    draggable: false,
    data: { resource: r, level, monthlyUsd: c?.monthlyUsd ?? null, basis: c?.basis || null },
    width: NODE_W,
    height: NODE_H,
  };
}

function group(id, label, x, y, w, h, variant, parentId, meta) {
  return {
    id, type: 'infraGroup', position: { x, y }, width: w, height: h, draggable: false, selectable: !!meta,
    ...(parentId ? { parentId, extent: 'parent' } : {}),
    style: { width: w, height: h },
    data: { label, variant, meta },
  };
}

// Stack a list of cards in `cols` columns inside a box; returns height used.
function grid(cards, cols, x0, y0, out, parentId, costs) {
  cards.forEach((r, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    out.push(card(r, costs, x0 + col * (NODE_W + GAP), y0 + row * (NODE_H + GAP), parentId));
  });
  const rows = Math.ceil(cards.length / cols);
  return rows ? rows * (NODE_H + GAP) - GAP : 0;
}

export function buildInfraGraph(inventory, costs) {
  const all = (inventory?.resources || []).filter((r) => !HIDDEN_PREFIXES.some((p) => r.id.startsWith(p)));
  const byId = Object.fromEntries(all.map((r) => [r.id, r]));
  const nodes = [];
  const edges = [];
  const lanes = { client: [], edge: [], vpc: [], data: [], external: [] };
  for (const r of all) lanes[laneOf(r)].push(r);

  let x = 0;
  const laneTops = {};

  // Client lane.
  const colH = (list) => list.length * (NODE_H + GAP) - GAP;
  const simpleLane = (key, label) => {
    const list = lanes[key];
    if (!list.length) return;
    const w = NODE_W + PAD * 2;
    const h = HEAD + PAD + colH(list) + PAD;
    nodes.push(group(`lane:${key}`, label, x, 0, w, h, 'lane'));
    list.forEach((r, i) => nodes.push(card(r, costs, PAD, HEAD + PAD + i * (NODE_H + GAP), `lane:${key}`)));
    laneTops[key] = { x, w };
    x += w + GAP * 2;
  };

  simpleLane('client', 'Client');
  simpleLane('edge', 'Edge');

  // VPC / host container.
  const vpcRes = lanes.vpc;
  if (vpcRes.length) {
    const container = byId.vpc || byId.host || null;
    const containerId = container ? container.id : 'lane:vpc';
    const inner = vpcRes.filter((r) => r.id !== containerId);
    const subnets = inner.filter((r) => r.id.startsWith('subnet:')).sort((a, b) => Number(!!b.details?.public) - Number(!!a.details?.public) || String(a.details?.az).localeCompare(String(b.details?.az)));
    const nats = inner.filter((r) => r.id.startsWith('nat:'));
    const ingress = inner.filter((r) => r.id === 'alb' || r.id.startsWith('alb:') || r.id.startsWith('igw:'));
    const services = inner.filter((r) => ['ecs-cluster', 'ecs-prod', 'ecs-uat', 'runners'].includes(r.id) || r.id.startsWith('svc:') || (r.category === 'compute' && !r.addon && !r.id.startsWith('nat:')));
    const stacks = inner.filter((r) => r.id.startsWith('cfn:') || r.addon);
    const rest = inner.filter((r) => ![...subnets, ...nats, ...ingress, ...services, ...stacks].includes(r));
    const cols = Math.max(2, Math.min(4, subnets.length || 3, Math.max(services.length, stacks.length, rest.length, 2)));
    const subnetMinW = subnets.length ? subnets.length * (NODE_W + PAD * 2) + GAP * (subnets.length - 1) : 0;
    const innerW = Math.max(cols * (NODE_W + GAP) - GAP, subnetMinW);
    let y = HEAD + PAD;
    const place = (list, c = cols) => { const h = grid(list, c, PAD, y, nodes, containerId, costs); if (h) y += h + GAP * 1.5; };
    place(ingress);
    // Subnets as sub-groups, side by side, NATs inside their subnet.
    if (subnets.length) {
      const sw = Math.max(NODE_W + PAD * 2, Math.floor((innerW - GAP * (subnets.length - 1)) / subnets.length));
      const natH = nats.length ? NODE_H + GAP : 0;
      const sh = HEAD + PAD + natH + PAD;
      subnets.forEach((s, i) => {
        const sx = PAD + i * (sw + GAP);
        const d = s.details || {};
        const label = `${d.public ? 'public' : 'private'} · ${d.az || ''} · ${d.cidr || ''}`;
        nodes.push(group(s.id, label, sx, y, sw, sh, d.public ? 'subnet public' : 'subnet private', containerId, s));
        nats.filter((n) => n.parent === s.id).forEach((n, k) => nodes.push(card(n, costs, PAD + k * (NODE_W + GAP), HEAD + PAD, s.id)));
      });
      // Orphan NATs (subnet not in the service's set).
      const orphans = nats.filter((n) => !subnets.some((s) => s.id === n.parent));
      y += sh + GAP * 1.5;
      place(orphans);
    }
    place(services);
    place(stacks);
    place(rest);
    const w = innerW + PAD * 2;
    const h = y - GAP * 1.5 + PAD;
    const label = container ? (container.id === 'host' ? `Host · ${container.name}` : `VPC · ${container.details?.cidr || ''} · ${container.name}`) : (inventory?.runtime === 'docker' ? 'Host' : 'Network');
    nodes.unshift(group(containerId, label, x, 0, w, h, container?.id === 'host' ? 'host' : 'vpc', null, container));
    laneTops.vpc = { x, w };
    x += w + GAP * 2;
  }

  simpleLane('data', 'Data & storage');
  simpleLane('external', 'External');

  // Edges — fixed story for the baseline, generic rules for the rest.
  const has = (id) => !!byId[id] && nodes.some((n) => n.id === id);
  const link = (a, b, opts = {}) => { if (has(a) && has(b)) edges.push({ id: `${a}->${b}`, source: a, target: b, type: 'infraEdge', data: opts }); };
  const api = has('alb') ? 'alb' : has('svc:caddy') ? 'svc:caddy' : null;
  link('client', 'hosting'); link('client', 'auth'); link('client', 'cdn');
  if (api) link('client', api, { label: 'HTTPS' });
  for (const g of all.filter((r) => r.id.startsWith('igw:'))) link(g.id, 'alb');
  const app = has('ecs-prod') ? 'ecs-prod' : has('svc:backend') ? 'svc:backend' : null;
  if (api && app) link(api, app, { label: '/health' });
  link('cdn', 's3-public');
  if (app) for (const t of ['firestore', 's3-raw', 's3-public', 'secrets', 'logs', 'ses', 'runners', 'ai', 'plane', 'agents', 'svc:db', 'svc:auth', 'svc:rest', 'svc:realtime', 'svc:minio', 'svc:vllm', 'svc:ollama', 'svc:stalwart']) link(app, t);
  link('ecs-uat', 'firestore', { dashed: true });
  link('codebuild', 'ecr'); link('ecr', 'ecs-prod', { dashed: true }); link('s3-src', 'codebuild', { dashed: true });
  for (const n of all.filter((r) => r.id.startsWith('nat:'))) for (const g of all.filter((r) => r.id.startsWith('igw:'))) link(n.id, g.id, { dashed: true });
  for (const r of all.filter((r) => r.addon && r.source !== 'discovered')) if (app) link(app, r.id, { dashed: true, planned: true });
  for (const r of all.filter((r) => r.id.startsWith('cfn:'))) if (app) link(app, r.id, { dashed: true });

  return { nodes, edges, lanes: laneTops };
}
